import { Injectable, signal, computed, effect } from '@angular/core';
import themes from 'devextreme/ui/themes';

export type Theme = 'light' | 'dark';

const DX_THEME: Record<Theme, string> = {
  light: 'generic.light',
  dark: 'generic.dark',
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'compleet_theme';

  private readonly _theme = signal<Theme>(this.resolveInitialTheme());

  readonly theme = this._theme.asReadonly();
  readonly isDark = computed(() => this._theme() === 'dark');

  constructor() {
    effect(() => {
      this.applyTheme(this._theme());
    });
  }

  setTheme(theme: Theme): void {
    this._theme.set(theme);
    localStorage.setItem(this.STORAGE_KEY, theme);
  }

  toggleTheme(): void {
    this.setTheme(this._theme() === 'light' ? 'dark' : 'light');
  }

  private resolveInitialTheme(): Theme {
    const stored = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private applyTheme(theme: Theme): void {
    document.body.classList.remove('compleet-theme-light', 'compleet-theme-dark');
    document.body.classList.add(`compleet-theme-${theme}`);

    const wanted = DX_THEME[theme];
    // Ativa o stylesheet DevExtreme do tema atual e desativa o outro (ambos
    // carregados no index.html). Isso, e não themes.current() sozinho, é o que
    // troca o visual dos componentes DevExtreme em runtime.
    document
      .querySelectorAll<HTMLLinkElement>('link.dx-theme-css[data-theme]')
      .forEach(link => {
        link.disabled = link.dataset['theme'] !== wanted;
      });

    // Informa o DevExtreme JS (para componentes criados depois). themes.ready()
    // adia até o CSS estar carregado/detectável, evitando o E0021 do bootstrap;
    // o try/catch protege caso o tema ainda não esteja registrado.
    themes.ready(() => {
      try {
        themes.current(wanted);
      } catch {
        /* o CSS já foi alternado via `disabled` — nada a fazer */
      }
    });
  }
}
